import React, { FC, useMemo } from "react"
import { useSnapshot } from "valtio"
import { DomNodes } from "../../constants"
import { CurrentTestState } from "../../storage"
import {
  useSelectableChoice,
  transformChoicesConstraints,
  useFindNodes,
  useAssessmentItemResponseScoring
  // getElementFeedbackItems,
} from "../../helpers"
import { HotspotArea } from "../HotspotArea"
import { ConstraintsAlertContainer } from "../../ui/containers/ConstraintsAlertContainer"
import {
  Cardinality,
  CheckCorrect,
  InteractionType,
  isQuestionComplete,
  onSaveStudentAnswer,
  Variant,
  ContainerType,
  NeutralSetting
} from "../../types"

interface HotspotInteractionProps {
  itemId: string
  responseidentifier: string
  minchoices?: string
  maxchoices?: string
  children: JSX.Element
  className?: string
  accessibilityAttr?: { [key: string]: string }
  onSaveStudentAnswer?: onSaveStudentAnswer
  checkCorrect?: CheckCorrect
  isCompletedQuestion?: isQuestionComplete
  containerType: ContainerType
  isNeutralSetting?: NeutralSetting
}

export const HotspotInteraction: FC<HotspotInteractionProps> = props => {
  const {
    children,
    className,
    minchoices,
    maxchoices,
    responseidentifier,
    accessibilityAttr,
    onSaveStudentAnswer,
    isCompletedQuestion,
    checkCorrect,
    containerType,
    isNeutralSetting
  } = props
  const [minChoices, maxChoices] = transformChoicesConstraints(
    minchoices,
    maxchoices
  )

  const { questionCompleted } = isCompletedQuestion?.() ?? {
    questionCompleted: false
  }

  const [[prompt], [image], hotspotChoices] = useFindNodes(children, [
    DomNodes.prompt,
    DomNodes.object,
    DomNodes.hotspotChoice
  ])

  const {
    selected,
    onSelect: onToggle,
    isHotspotProcessing,
    responseDeclaration
  } = useSelectableChoice({
    responseIdentifier: responseidentifier,
    interactionType: InteractionType.hotspot,
    constraints: {
      minChoices,
      maxChoices
    },
    onSaveStudentAnswer
  })
  const { currentItemResponse } = useSnapshot(CurrentTestState)
  const { scoring, groupMode, correctResponse } =
    useAssessmentItemResponseScoring(
      currentItemResponse?.itemId,
      responseidentifier
    )

  const maxSelected =
    containerType === "assessment"
      ? maxChoices > 1 && selected.length === maxChoices
      : null

  const allowMultiple =
    responseDeclaration?.cardinality === Cardinality.multiple ||
    (isNeutralSetting && minChoices > 1)

  // a11y: multi (checkbox/group) unless the item is capped at exactly one choice —
  // mirrors ChoiceInteraction and useSelectableChoice (maxChoices 0 = unlimited).
  const availableMultipleChoices = allowMultiple && maxChoices !== 1

  // a11y: name the region set by the prompt so the group announces its name (and, for
  // single-select radiogroups, "N of M") — mirrors ChoiceInteraction. Gate on a real prompt
  // element: a prompt-less hotspot (question lives in a passage) must fall back to a plain
  // aria-label, not an aria-labelledby pointing at an id that never renders.
  const hasPrompt = React.isValidElement(prompt)

  const promptId = useMemo(() => {
    if (!hasPrompt || !props.itemId || !responseidentifier) return null
    return `${props.itemId}-${responseidentifier}-prompt`
  }, [hasPrompt, props.itemId, responseidentifier])

  const promptWithId = useMemo(() => {
    if (!promptId || !React.isValidElement(prompt)) return prompt
    return React.cloneElement(prompt, { id: promptId } as React.Attributes)
  }, [prompt, promptId])

  return (
    <div className={className} {...accessibilityAttr}>
      {promptWithId}
      {containerType === "assessment" && (
        <ConstraintsAlertContainer
          minChoices={minChoices}
          maxChoices={maxChoices}
          selectedCount={selected.length}
        />
      )}
      <HotspotArea
        image={image}
        svgProps={{
          role: availableMultipleChoices ? "group" : "radiogroup",
          ...(promptId
            ? { "aria-labelledby": promptId }
            : { "aria-label": "Answer regions" })
        }}
      >
        {hotspotChoices.map((hotspotChoice, index) => {
          const identifier = hotspotChoice.props.identifier
          const isSelected =
            selected.includes(identifier) ||
            (questionCompleted &&
              !isNeutralSetting &&
              checkCorrect?.(identifier))
          const correct = scoring?.response?.includes(identifier)

          // a11y: the badge variant is the correctness the student actually sees — in lessons
          // it comes from checkCorrect on selected regions, and it also drives the visual
          // badge, so deriving the name from it keeps name and badge in lockstep.
          const variant: Variant | null =
            isSelected && isNeutralSetting
              ? Variant.neutral
              : isSelected && checkCorrect
              ? checkCorrect(identifier)
                ? Variant.correct
                : Variant.invalid
              : null

          // a11y: the region's accessible name — authored label ("1", "2", …), else a
          // human-readable "Region N" for blank labels; then the revealed correctness
          // ("Correct."/"Incorrect."), then "N of M" for multi-select checkboxes only
          // (radios get it natively from the radiogroup).
          const rawHotspotLabel = hotspotChoice.props.hotspotlabel
          const regionLabel =
            (typeof rawHotspotLabel === "string" && rawHotspotLabel.trim()) ||
            `Region ${index + 1}`

          // a11y: mirror the badge's OWN correct/incorrect formula (`correct || variant===correct`
          // in HotspotChoice) so the announced name can never contradict the visual ✓/✗. The two
          // answer-key sources (scoring `correct` vs `checkCorrect`) can disagree in a
          // multi-interaction item; the name must show whatever the badge shows. Group aggregate
          // and neutral items suppress correctness.
          const showsCorrectness =
            !groupMode &&
            !isNeutralSetting &&
            (correct !== undefined || variant !== null)
          const correctnessSuffix = !showsCorrectness
            ? ""
            : correct || variant === Variant.correct
            ? ", Correct"
            : ", Incorrect"
          const positionSuffix = availableMultipleChoices
            ? `, ${index + 1} of ${hotspotChoices.length}`
            : ""
          const accessibleName = `${regionLabel}${correctnessSuffix}${positionSuffix}`

          const selectedItem = groupMode
            ? correctResponse?.includes(identifier)
            : isSelected

          // const feedbackItems = groupMode
          //   ? getElementFeedbackItems(hotspotChoice, groupStatistics, correctResponse)
          //   : undefined

          return (
            <hotspotChoice.type
              key={identifier}
              {...hotspotChoice.props}
              selected={selectedItem}
              disabled={
                (!isSelected && maxSelected) ||
                questionCompleted ||
                isHotspotProcessing
              }
              correct={isNeutralSetting ? undefined : correct}
              groupMode={groupMode}
              // feedbackItems={feedbackItems}
              onToggle={() => !questionCompleted && onToggle(identifier)}
              role={availableMultipleChoices ? "checkbox" : "radio"}
              accessibleName={accessibleName}
              variant={variant}
              isNeutralSetting={isNeutralSetting}
            />
          )
        })}
      </HotspotArea>
    </div>
  )
}
